/**
 * Single source of truth for chunk record freshness predicates.
 *
 * Before this module, freshness checks were scattered across 5+ sites in Reader +
 * Backend with subtle variations (missing status-exempt, missing protection check,
 * etc.). See the migration plan at [project_recent_changes.md] for inventory.
 *
 * All predicates are pure functions over a chunk record. The record shape:
 *   { prompt_version, llm_model, manual_review_status, status, ... }
 *
 * "Protected" = Opus-generated OR admin-reviewed-passed. These records are locked
 * from auto-invalidation (curated content protection).
 */

/**
 * True iff the record is protected from auto-invalidation.
 * Semantics: either the model is Opus (high-confidence) or the record has been
 * manually passed by an admin. Mirrors the write-side invariant in
 * admin-hot-upgrade.ts chunk-import which always sets both.
 */
export function isProtected(record) {
  if (!record) return false;
  return record.llm_model === 'claude-opus' || record.manual_review_status === 'passed';
}

/**
 * True iff the record's prompt_version matches the given minimum version.
 * When minVersion is null/undefined, treat as fresh (don't invalidate).
 */
export function isVersionFresh(record, minVersion) {
  if (!minVersion) return true;
  return record?.prompt_version === minVersion;
}

/**
 * True iff a local cached record should be invalidated given the current minVersion.
 * Used by: local prune (chunkApi.pruneStaleLocalChunks) AND the local-hit staleness
 * check in useChunkAnnotations. Protection-aware: admin-passed/opus records stay.
 */
export function shouldInvalidateLocal(record, minVersion) {
  return !isVersionFresh(record, minVersion) && !isProtected(record);
}

/**
 * True iff a cloud-returned record should be skipped (e.g., stale version).
 * Used by: cloud response filter in fetchChapterChunksFromCloud.
 * Differs from shouldInvalidateLocal by also exempting in-flight reprocessing
 * (status='processing') — we want to surface partial progress to the caller.
 */
export function shouldSkipCloudResult(record, minVersion) {
  if (!record) return false;
  return !isVersionFresh(record, minVersion)
    && record.status !== 'processing'
    && !isProtected(record);
}

/**
 * True iff a local record is worth re-fetching from cloud during reconcile.
 * Used by: reconcileBookChunks. Ignores version (we're looking for model or
 * review-status upgrades that may have happened at the same version).
 */
export function isUpgradeCandidate(record) {
  return !isProtected(record);
}
